import { Atom, Library, LogOut, X } from "lucide-react";
import { useAuth } from "./AuthGate";
import CaptureIcon from "./IconComponent/CaptureIcon";
import TrainingMenuIcon from "./IconComponent/TrainingMenuIcon";
import QuestIcon from "./IconComponent/QuestIcon";
import GameIcon from "./IconComponent/GameIcon";
import GemIcon from "./IconComponent/GemIcon";
import MedalsIcon from "./IconComponent/MedalsIcon";
import ChartPieIcon from "./IconComponent/ChartPieIcon";
import MenuIconTooltip from "./MenuIconTooltip";
import MenuIconBadge from "./MenuIconBadge";
import RegisterWorkspaceSwitch from "./RegisterWorkspaceSwitch";
import AccountMenuSwitch from "./AccountMenuSwitch";
import { TRAINING_MODE_FREE, TRAINING_MODE_QUEST } from "../constants/entrainementSetup";
import { MENU_LIBRARY_TOOLTIPS, MENU_MODE_TOOLTIPS, MENU_TRAINING_TOOLTIPS } from "../constants/menuTooltips";
import { REGISTER_WORKSPACE_MODES } from "../utils/registerWorkspacePreference";
import { ACCOUNT_MENU_MODES } from "../utils/accountMenuPreference";

const MENU_ITEMS = [
  { mode: "capture", Icon: CaptureIcon },
  { mode: "training" },
  { mode: "quest", Icon: QuestIcon, badgeKey: "quests" },
  { mode: "game", Icon: GameIcon },
  { mode: "collection", Icon: GemIcon, badgeKey: "cloverRewards" },
  { mode: "medals", Icon: MedalsIcon, badgeKey: "medals" },
  { mode: "stats", Icon: ChartPieIcon },
];

export default function Menu({
  onChangeMode = () => {},
  activeMode = "register",
  badgeCounts = {},
  registerWorkspaceMode = REGISTER_WORKSPACE_MODES.REGISTER,
  onRegisterWorkspaceChange = () => {},
  accountMenuMode = ACCOUNT_MENU_MODES.LOGOUT,
  onAccountMenuWorkspaceChange = () => {},
  trainingMode = TRAINING_MODE_FREE,
  libraryAtHome = true,
  onLibraryHome = () => {},
  highlightTool = null,
  onHighlightTool = () => {},
}) {
  const { logout, user } = useAuth();
  const registerSectionActive = activeMode === "register";

  if (activeMode === "library") {
    return (
      <div className={libraryAtHome ? "menuContainer" : "menuContainer menuContainer--library-read"}>
        {!libraryAtHome ? (
          <>
            <MenuIconTooltip
              tooltip={MENU_LIBRARY_TOOLTIPS.home}
              placement="below"
              className="icon"
            >
              <Library
                data-library-home=""
                size={50}
                color="#39210d"
                strokeWidth={1}
                onClick={onLibraryHome}
                aria-label={MENU_LIBRARY_TOOLTIPS.home}
                style={{ cursor: "pointer" }}
              />
            </MenuIconTooltip>
            <MenuIconTooltip tooltip={MENU_LIBRARY_TOOLTIPS.passage} placement="below">
              <button
                type="button"
                className={
                  highlightTool === "passage"
                    ? "library-mark-tool library-mark-tool--on"
                    : "library-mark-tool"
                }
                aria-pressed={highlightTool === "passage"}
                aria-label={MENU_LIBRARY_TOOLTIPS.passage}
                onClick={() => onHighlightTool("passage")}
              >
                <span className="library-mark-tool__dot library-mark-tool__dot--passage" />
              </button>
            </MenuIconTooltip>
            <MenuIconTooltip tooltip={MENU_LIBRARY_TOOLTIPS.word} placement="below">
              <button
                type="button"
                className={
                  highlightTool === "word"
                    ? "library-mark-tool library-mark-tool--on"
                    : "library-mark-tool"
                }
                aria-pressed={highlightTool === "word"}
                aria-label={MENU_LIBRARY_TOOLTIPS.word}
                onClick={() => onHighlightTool("word")}
              >
                <span className="library-mark-tool__dot library-mark-tool__dot--word" />
              </button>
            </MenuIconTooltip>
            <MenuIconTooltip tooltip={MENU_LIBRARY_TOOLTIPS.favorite} placement="below">
              <button
                type="button"
                className={
                  highlightTool === "favorite"
                    ? "library-mark-tool library-mark-tool--on"
                    : "library-mark-tool"
                }
                aria-pressed={highlightTool === "favorite"}
                aria-label={MENU_LIBRARY_TOOLTIPS.favorite}
                onClick={() => onHighlightTool("favorite")}
              >
                <span className="library-mark-tool__dot library-mark-tool__dot--favorite" />
              </button>
            </MenuIconTooltip>
          </>
        ) : null}
        <MenuIconTooltip
          tooltip={MENU_LIBRARY_TOOLTIPS.close}
          placement="below"
          className="library-menu-close-tip"
        >
          <button
            type="button"
            className="library-menu-close"
            onClick={() => onChangeMode("register")}
            aria-label={MENU_LIBRARY_TOOLTIPS.close}
          >
            <X size={22} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </MenuIconTooltip>
      </div>
    );
  }

  return (
    <div className="menuContainer">
      <RegisterWorkspaceSwitch
        workspaceMode={registerWorkspaceMode}
        sectionActive={registerSectionActive}
        onEnterSection={() => onChangeMode("register")}
        onWorkspaceChange={onRegisterWorkspaceChange}
      />

      <MenuIconTooltip
        tooltip={MENU_MODE_TOOLTIPS.groupings}
        placement="below"
        className={activeMode === "groupings" ? "icon active" : "icon"}
      >
        <Atom
          size={50}
          color="#39210d"
          strokeWidth={1}
          onClick={() => onChangeMode("groupings")}
          aria-label={MENU_MODE_TOOLTIPS.groupings}
          style={{ cursor: "pointer" }}
        />
      </MenuIconTooltip>

      {MENU_ITEMS.map(({ mode, Icon, badgeKey }) => {
        const badgeCount =
          badgeKey === "medals"
            ? badgeCounts.medals ?? 0
            : badgeKey === "cloverRewards"
              ? badgeCounts.cloverRewards ?? 0
              : badgeKey === "quests"
                ? badgeCounts.quests ?? 0
              : 0;
        const isQuestTraining = trainingMode === TRAINING_MODE_QUEST;
        const tooltip =
          mode === "training"
            ? isQuestTraining
              ? MENU_TRAINING_TOOLTIPS.quest
              : MENU_TRAINING_TOOLTIPS.free
            : MENU_MODE_TOOLTIPS[mode];

        return (
          <MenuIconTooltip
            key={mode}
            tooltip={tooltip}
            placement="below"
            className={activeMode === mode ? "icon active" : "icon"}
          >
            <span className="menu-icon-badge-wrap">
              {mode === "training" ? (
                <TrainingMenuIcon
                  size={50}
                  color="#39210d"
                  mode={trainingMode}
                  onClick={() => onChangeMode(mode)}
                />
              ) : (
                <Icon
                  size={50}
                  color="#39210d"
                  strokeWidth={1}
                  onClick={() => onChangeMode(mode)}
                />
              )}
              <MenuIconBadge count={badgeCount} />
            </span>
          </MenuIconTooltip>
        );
      })}

      {user?.admin ? (
        <AccountMenuSwitch
          workspaceMode={accountMenuMode}
          sectionActive={activeMode === "cpanel"}
          onEnterSection={() => onChangeMode("cpanel")}
          onWorkspaceChange={onAccountMenuWorkspaceChange}
          onLogout={logout}
        />
      ) : (
        <MenuIconTooltip
          tooltip={MENU_MODE_TOOLTIPS.logout}
          placement="below"
          className="icon"
        >
          <LogOut
            size={50}
            color="#39210d"
            strokeWidth={1}
            onClick={logout}
            aria-label={MENU_MODE_TOOLTIPS.logout}
            style={{ cursor: "pointer" }}
          />
        </MenuIconTooltip>
      )}
    </div>
  );
}
